import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { gamesApi, venuesApi } from '../services/api';
import { exportCSV } from '../utils/export';
import './Reports.css';

function Reports({ user, onLogout }) {
  const [reports, setReports] = useState([]);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('reports');

  // Filter state
  const [venueId, setVenueId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Venue analytics
  const [venueStats, setVenueStats] = useState([]);

  useEffect(() => {
    fetchVenues();
    fetchReports();
  }, []);

  const fetchVenues = async () => {
    try {
      const res = await venuesApi.getAll();
      const list = res?.data?.venues || res?.venues || res?.data || [];
      setVenues(Array.isArray(list) ? list : []);
    } catch (err) {
      console.warn('Could not fetch venues:', err);
    }
  };

  const fetchReports = async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const res = await gamesApi.getReports(filters);
      const list = res?.data?.reports || res?.reports || [];
      const reportList = Array.isArray(list) ? list : [];
      setReports(reportList);

      // Calculate venue analytics from reports
      const venueMap = {};
      reportList.forEach(r => {
        const name = r.venue?.name || 'Unknown';
        if (!venueMap[name]) {
          venueMap[name] = { name, games: 0, totalPlayers: 0, totalResults: 0, topAttendance: 0 };
        }
        venueMap[name].games++;
        const playerCount = r.results?.length || 0;
        venueMap[name].totalPlayers += playerCount;
        venueMap[name].totalResults += r.results?.length || 0;
        if (playerCount > venueMap[name].topAttendance) venueMap[name].topAttendance = playerCount;
      });
      const stats = Object.values(venueMap).map(v => ({
        ...v,
        avgPlayers: v.games > 0 ? Math.round(v.totalPlayers / v.games * 10) / 10 : 0,
      }));
      stats.sort((a, b) => b.totalPlayers - a.totalPlayers);
      setVenueStats(stats);
    } catch (err) {
      console.error('Error fetching reports:', err);
      setError(err.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const filters = {};
    if (venueId) filters.venueId = venueId;
    if (dateFrom) filters.dateFrom = dateFrom;
    if (dateTo) filters.dateTo = dateTo;
    fetchReports(filters);
  };

  const handleClear = () => {
    setVenueId('');
    setDateFrom('');
    setDateTo('');
    fetchReports();
  };

  const handleExportReports = () => {
    const rows = [];
    reports.forEach(r => {
      (r.results || []).forEach(result => {
        rows.push([
          r.venue?.name || '', formatDate(r.startTime, r.leagueTimezone),
          r.gameType, result.playerName, result.place, result.pointsAwarded || 0,
          (r.dealers || []).map(d => d.name).join('; '),
        ]);
      });
    });
    exportCSV('game-reports.csv',
      ['Venue', 'Date', 'Game Type', 'Player', 'Place', 'Points', 'Dealers'],
      rows
    );
  };

  const formatDate = (dateStr, timeZone) => {
    const date = new Date(dateStr);
    const options = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };
    if (timeZone) options.timeZone = timeZone;
    return date.toLocaleString('en-US', options);
  };

  const getPlaceIcon = (place) => {
    if (place === 1) return '1st';
    if (place === 2) return '2nd';
    if (place === 3) return '3rd';
    const s = ['th', 'st', 'nd', 'rd'];
    const v = place % 100;
    return place + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  return (
    <div className="admin-layout">
      <Sidebar user={user} onLogout={onLogout} />

      <main className="main-content">
        <div className="page-header">
          <div>
            <h2>Reports & Analytics</h2>
            <p>Game results, venue performance, and attendance data</p>
          </div>
          {reports.length > 0 && (
            <button className="btn btn-primary" onClick={handleExportReports}>Export CSV</button>
          )}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
          <button className={`btn ${activeTab === 'reports' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setActiveTab('reports')}>
            Game Reports ({reports.length})
          </button>
          <button className={`btn ${activeTab === 'venues' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setActiveTab('venues')}>
            Venue Analytics
          </button>
        </div>

        {/* Filter Bar */}
        <div className="card reports-filter-bar">
          <form onSubmit={handleSearch} className="reports-filters">
            <div className="filter-group">
              <label htmlFor="filterVenue">Venue</label>
              <select id="filterVenue" className="form-select" value={venueId} onChange={(e) => setVenueId(e.target.value)}>
                <option value="">All Venues</option>
                {venues.map(v => (<option key={v.id} value={v.id}>{v.name}</option>))}
              </select>
            </div>
            <div className="filter-group">
              <label htmlFor="filterDateFrom">From</label>
              <input type="date" id="filterDateFrom" className="form-input" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div className="filter-group">
              <label htmlFor="filterDateTo">To</label>
              <input type="date" id="filterDateTo" className="form-input" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
            <div className="filter-actions">
              <button type="submit" className="btn btn-primary btn-sm">Search</button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={handleClear}>Clear</button>
            </div>
          </form>
        </div>

        {loading && (<div className="loading"><div className="loading-spinner"></div></div>)}
        {error && !loading && (<div className="card"><div className="error-message">{error}</div></div>)}

        {/* Venue Analytics Tab */}
        {!loading && activeTab === 'venues' && (
          <div className="card">
            <div className="card-header">
              <h3>Venue Performance</h3>
              {venueStats.length > 0 && (
                <button className="btn btn-secondary btn-sm" onClick={() => exportCSV(
                  'venue-analytics.csv',
                  ['Venue', 'Games Played', 'Total Players', 'Avg Players', 'Peak Attendance'],
                  venueStats.map(v => [v.name, v.games, v.totalPlayers, v.avgPlayers, v.topAttendance])
                )}>Export CSV</button>
              )}
            </div>
            {venueStats.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">📊</div>
                <h3>No data yet</h3>
                <p>Venue analytics will appear after games are completed.</p>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Venue</th>
                    <th>Games</th>
                    <th>Total Players</th>
                    <th>Avg Players</th>
                    <th>Peak</th>
                  </tr>
                </thead>
                <tbody>
                  {venueStats.map(v => (
                    <tr key={v.name}>
                      <td><strong>{v.name}</strong></td>
                      <td>{v.games}</td>
                      <td>{v.totalPlayers}</td>
                      <td>{v.avgPlayers}</td>
                      <td>{v.topAttendance}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Game Reports Tab */}
        {!loading && !error && activeTab === 'reports' && reports.length === 0 && (
          <div className="card">
            <div className="empty-state">
              <div className="empty-state-icon">📋</div>
              <h3>No completed games found</h3>
              <p>Completed game reports will appear here. Try adjusting your filters.</p>
            </div>
          </div>
        )}

        {!loading && !error && activeTab === 'reports' && reports.length > 0 && (
          <div className="reports-list">
            {reports.map(report => (
              <div key={report.id} className="card report-card">
                <div className="report-header">
                  <div className="report-venue">
                    <h3>{report.venue.name}</h3>
                    <span className="report-address">{report.venue.address}</span>
                  </div>
                  <div className="report-meta">
                    <span className="report-date">{formatDate(report.startTime, report.leagueTimezone)}</span>
                    <span className="report-type">{report.gameType}</span>
                  </div>
                </div>
                {report.dealers.length > 0 && (
                  <div className="report-dealers">
                    <span className="report-section-label">Dealers:</span>
                    {report.dealers.map((d, i) => (<span key={d.id || i} className="badge badge-info">{d.name}</span>))}
                  </div>
                )}
                {report.results.length > 0 ? (
                  <table className="data-table report-results-table">
                    <thead><tr><th>Place</th><th>Player</th><th>Points</th></tr></thead>
                    <tbody>
                      {report.results.map((result, index) => (
                        <tr key={result.playerId || index}>
                          <td><span className={`place-badge place-${result.place}`}>{getPlaceIcon(result.place)}</span></td>
                          <td><strong>{result.playerName}</strong></td>
                          <td>{result.pointsAwarded || 0} pts</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="report-no-results">No player results recorded</p>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default Reports;
